/**
 * TourRequestsTab Component - Display buyer's tour requests
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTourRequests } from '../../agents/tourRequestAgent';
import APIAgent from '../../agents/apiAgent';
import TourFeedback from './TourFeedback';
import { TourActionModal } from './modals';
import tourErrorMessage from './tourErrorMessage';
import { genCopy } from '../OfferManagement/notifications/genericNotificationCopy';
import { hasTourStarted, tourStatusLabel, TOUR_NOT_STARTED_TEXT } from './tourTime';
import './TourRequestsTab.css';

const PAGE_SIZE_OPTIONS = [6, 12, 24];
const DEFAULT_PAGE_SIZE = 6;
// `notes` code written when a listing delete cancels the tour
// (TOUR_CANCEL_REASON in backend/app/services/listing_deletion.py).
const LISTING_DELETED_NOTE = 'listing_deleted';

/** The cancellation reason as the buyer should read it. */
const cancellationReason = (notes) =>
  notes === LISTING_DELETED_NOTE
    ? genCopy('GEN.notification.tour_cancelled_listing_deleted.text')
    : notes;

const TourRequestsTab = ({ buyerId }) => {
  const { tours, loading, error, fetchTours, cancelTour, completeTour, submitTourFeedback } = useTourRequests(buyerId);
  const [houses, setHouses] = useState([]);
  const [displayTours, setDisplayTours] = useState([]);
  // The confirmation modal currently open: { type: 'cancel' | 'complete', tour }.
  const [action, setAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    fetchTours();
  }, [buyerId, fetchTours]);

  // Fetch house details for each tour by house_id
  useEffect(() => {
    if (!tours || tours.length === 0) {
      setHouses([]);
      return;
    }

    const loadHouses = async () => {
      try {
        const housesFromPayload = tours
          .map((t) => t.house)
          .filter(Boolean);

        const unresolvedIds = [
          ...new Set(
            tours
              .filter((t) => !t.house && t.house_id)
              .map((t) => t.house_id)
          ),
        ];

        if (unresolvedIds.length === 0) {
          setHouses(housesFromPayload);
          return;
        }

        const results = await Promise.all(
          unresolvedIds.map((id) => APIAgent.request(`/api/houses/${id}`, 'GET').catch(() => null))
        );
        const merged = [...housesFromPayload, ...results.filter(Boolean)];
        const dedupedById = [...new Map(merged.map((h) => [h.id, h])).values()];
        setHouses(dedupedById);
      } catch (err) {
        console.error('Error fetching houses for tours:', err);
      }
    };
    loadHouses();
  }, [tours]);

  useEffect(() => {
    const enrichedTours = tours.map((tour) => {
      const house = houses.find((h) => h.id === tour.house_id);
      return { ...tour, house };
    });
    setDisplayTours(enrichedTours);
  }, [tours, houses]);

  // Sort newest-first then paginate (client-side: API returns the full list).
  const sortedTours = useMemo(
    () =>
      [...displayTours].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      }),
    [displayTours]
  );

  const totalPages = Math.max(1, Math.ceil(sortedTours.length / pageSize));

  // Clamp page when the underlying list shrinks (e.g. after a cancel).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageStart = (page - 1) * pageSize;
  const pagedTours = sortedTours.slice(pageStart, pageStart + pageSize);
  const showingFrom = sortedTours.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + pageSize, sortedTours.length);

  const openAction = (type, tour) => {
    setActionError(null);
    setAction({ type, tour });
  };

  const closeAction = () => {
    setAction(null);
    setActionError(null);
  };

  const handleConfirmAction = async (reason) => {
    if (!action) return;
    setActionBusy(true);
    setActionError(null);
    try {
      if (action.type === 'cancel') {
        await cancelTour(action.tour.id, reason);
      } else {
        await completeTour(action.tour.id);
      }
      setAction(null);
    } catch (err) {
      setActionError(
        tourErrorMessage(
          err,
          action.type === 'cancel' ? 'Error cancelling tour' : 'Error completing tour'
        )
      );
    } finally {
      setActionBusy(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = `status-${status}`;
    return <span className={`status-badge ${statusClass}`}>{tourStatusLabel(status)}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    // Backend stores naive wall-clock datetimes. Strip any trailing 'Z' so JS
    // parses as local time and displays exactly what the seller/buyer chose.
    const normalized = String(dateStr).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    const date = new Date(normalized);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="tour-requests-tab loading">Loading...</div>;
  }

  return (
    <div className="tour-requests-tab">
      <div className="tours-header">
        <h2>Tour Requests</h2>
        <span className="count">{displayTours.length}</span>
      </div>

      {error && <div className="error-message">{error}</div>}

      {displayTours.length === 0 ? (
        <div className="empty-state">
          <p>No tour requests yet. Schedule tours to see them here!</p>
        </div>
      ) : (
        <>
        <div className="tours-list">
          {pagedTours.map((tour) => (
            <div key={tour.id} className="tour-card">
              <div className="tour-card-image">
                {tour.house?.image_urls && tour.house.image_urls[0] ? (
                  <img
                    src={APIAgent.getImageUrl(tour.house.image_urls[0])}
                    alt={tour.house.address}
                  />
                ) : (
                  <div className="tour-card-image-placeholder">
                    <span>🏠</span>
                    <p>No photo available</p>
                  </div>
                )}
              </div>
              <div className="tour-card-body">
              <div className="tour-header">
                <h3>{tour.house?.address || 'Unknown Property'}</h3>
                {getStatusBadge(tour.status)}
              </div>

              {tour.house && (
                <div className="tour-location">
                  {tour.house.city}, {tour.house.state} {tour.house.zip_code}
                </div>
              )}

              <div className="tour-details">
                <div className="detail-item">
                  <span className="label">Requested:</span>
                  <span className="value">
                    {new Date(tour.created_at).toLocaleDateString()}
                  </span>
                </div>

                {tour.scheduled_date && (
                  <div className="detail-item">
                    <span className="label">Scheduled:</span>
                    <span className="value">{formatDate(tour.scheduled_date)}</span>
                  </div>
                )}

                {!tour.scheduled_date
                  && (tour.status === 'pending' || tour.status === 'seller_approved')
                  && Array.isArray(tour.preferred_dates_json)
                  && tour.preferred_dates_json.length > 0 && (
                  <div className="detail-item">
                    <span className="label">You suggested:</span>
                    <span className="value">
                      {tour.preferred_dates_json.map(formatDate).join(', ')}
                    </span>
                  </div>
                )}

                {tour.status === 'seller_approved' && (
                  <div className="detail-item">
                    <span className="label">Next step:</span>
                    <span className="value">The seller approved your request and will confirm a time.</span>
                  </div>
                )}

                {tour.scheduled_duration_minutes && (
                  <div className="detail-item">
                    <span className="label">Duration:</span>
                    <span className="value">
                      {tour.scheduled_duration_minutes} minutes
                    </span>
                  </div>
                )}
              </div>

              {tour.message && (
                <div className="tour-message">
                  <strong>Your Message:</strong>
                  <p>{tour.message}</p>
                </div>
              )}

              {/* The decline / cancel dialogs say the reason is shared with the
                  buyer, so show it here, not only in the email. */}
              {tour.status === 'seller_declined' && tour.seller_notes && (
                <div className="tour-message">
                  <strong>Seller's note:</strong>
                  <p>{tour.seller_notes}</p>
                </div>
              )}
              {tour.status === 'cancelled' && tour.notes && (
                <div className="tour-message">
                  <strong>Cancellation reason:</strong>
                  <p>{cancellationReason(tour.notes)}</p>
                </div>
              )}

              <div className="tour-actions">
                {tour.status === 'scheduled' && (
                  <>
                    <button
                      className="btn-complete"
                      onClick={() => openAction('complete', tour)}
                      disabled={!hasTourStarted(tour)}
                      title={hasTourStarted(tour) ? undefined : TOUR_NOT_STARTED_TEXT}
                    >
                      Mark Complete
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={() => openAction('cancel', tour)}
                    >
                      Cancel Tour
                    </button>
                  </>
                )}
                {(tour.status === 'pending' || tour.status === 'seller_approved') && (
                  <button
                    className="btn-cancel"
                    onClick={() => openAction('cancel', tour)}
                  >
                    Cancel Request
                  </button>
                )}
              </div>

              {tour.status === 'completed' && !tour.buyer_rating && (
                <TourFeedback tourId={tour.id} onSubmit={submitTourFeedback} />
              )}
              </div>
            </div>
          ))}
        </div>
        <div className="tours-pagination" role="navigation" aria-label="Tour pagination">
          <div className="tours-pagination-info">
            Showing {showingFrom}–{showingTo} of {sortedTours.length}
          </div>
          <div className="tours-pagination-controls">
            <button
              type="button"
              className="page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              ← Prev
            </button>
            <span className="page-indicator">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              aria-label="Next page"
            >
              Next →
            </button>
            <label className="page-size-label">
              Per page:&nbsp;
              <select
                className="page-size-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        </>
      )}

      {action && (() => {
        const { type } = action;
        // Read the live row so late-loading house details still show.
        const tour = displayTours.find((t) => t.id === action.tour.id) || action.tour;
        const details = [
          { label: 'Property', value: tour.house?.address || `Property #${tour.house_id}` },
        ];
        if (tour.scheduled_date) {
          details.push({ label: 'Scheduled', value: formatDate(tour.scheduled_date) });
        }
        const isScheduled = tour.status === 'scheduled';
        return type === 'cancel' ? (
          <TourActionModal
            open
            title={isScheduled ? 'Cancel tour' : 'Cancel tour request'}
            prompt={
              isScheduled
                ? 'Cancel this scheduled tour? The seller will be notified.'
                : 'Withdraw this tour request? The seller will be notified.'
            }
            details={details}
            reasonLabel="Reason (optional, shared with the seller)"
            reasonPlaceholder="e.g., Schedule conflict"
            confirmLabel={isScheduled ? 'Cancel tour' : 'Cancel request'}
            busyLabel="Cancelling…"
            dismissLabel={isScheduled ? 'Keep tour' : 'Keep request'}
            tone="danger"
            submitting={actionBusy}
            error={actionError}
            onConfirm={handleConfirmAction}
            onClose={closeAction}
          />
        ) : (
          <TourActionModal
            open
            title="Mark tour complete"
            prompt="Mark this tour as completed? You'll be able to leave feedback afterwards."
            details={details}
            confirmLabel="Mark complete"
            busyLabel="Saving…"
            submitting={actionBusy}
            error={actionError}
            onConfirm={handleConfirmAction}
            onClose={closeAction}
          />
        );
      })()}
    </div>
  );
};

export default TourRequestsTab;
